import ExportButton from 'components/ExportButton/ExportButton';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Person } from 'types';
import { createCSVFileName, stripHtml } from 'utils/util/util';
import {
  type CalibrationComment,
  CalibrationCommentVisibilty,
  type CalibrationRecommendation,
  type CommentThreadType,
  CreateCommentHandler,
  EditCommentHandler,
  type ThreadedCalibrationComment,
  type UpdateCalibrationCommentPayload,
} from '../types';
import {
  flattenThreadedComments,
  getRecommendationDetails,
  isCalibrationRecommendation,
  RECOMMENDATION_FILTER_ORDER,
} from '../utils/comments';
import { CommentComposer, type CommentDraft } from './CommentComposer';
import { CommentFilters } from './CommentFilters';
import { CommentThread } from './CommentThread';

export type ToggleVisibilityHandler = (comment: CalibrationComment) => void;

export type CalibrationCommentsPopoverContentProps = {
  participant: Person;
  surveyResponseId: number;
  recommendation: CommentThreadType;
  comments: ThreadedCalibrationComment[];
  showPrivateComments: boolean;
  onToggleShowPrivate: (value: boolean) => void;
  canAddComment?: boolean;
  onSubmitComment: CreateCommentHandler;
  onToggleVisibility?: ToggleVisibilityHandler;
  onEditComment?: EditCommentHandler;
  currentUserId?: number;
  isViewAdmin?: boolean;
  variant?: 'popover' | 'modal';
  onUnsavedEditChange?: (hasUnsavedEdit: boolean) => void;
  onPendingDraftSaveChange?: (hasPendingDraftSave: boolean) => void;
  highlightedCommentId?: number;
};

const getRecommendationFilters = (
  recommendation: CommentThreadType
): Set<CalibrationRecommendation> => {
  const valid = isCalibrationRecommendation(recommendation);
  return valid
    ? new Set<CalibrationRecommendation>([valid])
    : new Set<CalibrationRecommendation>();
};

const getComposerDefaultRecommendation = (
  activeRecommendations: Set<CalibrationRecommendation>
): CalibrationRecommendation | undefined => {
  if (activeRecommendations.size === 0) {
    return undefined;
  }
  const firstMatch = RECOMMENDATION_FILTER_ORDER.find((option) =>
    activeRecommendations.has(option)
  );
  return firstMatch ?? undefined;
};

const CalibrationCommentsPopoverContent: React.FC<
  CalibrationCommentsPopoverContentProps
> = ({
  participant,
  surveyResponseId,
  recommendation,
  comments,
  showPrivateComments,
  onToggleShowPrivate,
  canAddComment = true,
  onSubmitComment,
  onToggleVisibility,
  onEditComment,
  currentUserId,
  isViewAdmin = false,
  variant = 'popover',
  onUnsavedEditChange,
  onPendingDraftSaveChange,
  highlightedCommentId,
}) => {
  const { formatMessage } = useIntl();
  const isModal = variant === 'modal';
  // null while the composer is closed
  const [composer, setComposer] = useState<CommentDraft | null>(null);
  const [activeRecommendations, setActiveRecommendations] = useState<
    Set<CalibrationRecommendation>
  >(() => getRecommendationFilters(recommendation));
  // Replies are collapsed by default, so open the linked comment's thread
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(() => {
    const highlighted = flattenThreadedComments(comments).find(
      (comment) => comment.id === highlightedCommentId
    );
    return new Set(highlighted ? [String(highlighted.thread_id)] : []);
  });
  const [activeReplyTarget, setActiveReplyTarget] = useState<string | null>(
    null
  );
  const [replyText, setReplyText] = useState<string>('');
  const [replyIsPrivate, setReplyIsPrivate] = useState<boolean>(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [isSubmittingReply, setIsSubmittingReply] = useState<boolean>(false);
  // The new comment and the open reply each report whether their draft is
  // still being saved, so the modal can warn before closing
  const [hasPendingCommentDraftSave, setHasPendingCommentDraftSave] =
    useState(false);
  const [hasPendingReplyDraftSave, setHasPendingReplyDraftSave] =
    useState(false);
  useEffect(() => {
    onPendingDraftSaveChange?.(
      hasPendingCommentDraftSave || hasPendingReplyDraftSave
    );
  }, [
    hasPendingCommentDraftSave,
    hasPendingReplyDraftSave,
    onPendingDraftSaveChange,
  ]);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState<boolean>(false);
  const [composerError, setComposerError] = useState<string | null>(null);
  const [isSubmittingComposer, setIsSubmittingComposer] =
    useState<boolean>(false);

  const csvFilename = createCSVFileName('calibration_comments');

  const csvData = useMemo(() => {
    if (!isModal) return [];

    type ExportRow = {
      comment: ThreadedCalibrationComment;
      depth: number;
      threadKey: string;
    };

    const rows: ExportRow[] = [];

    const visit = (
      comment: ThreadedCalibrationComment,
      depth: number,
      threadKey: string
    ) => {
      rows.push({ comment, depth, threadKey });
      comment.replies.forEach((reply) => visit(reply, depth + 1, threadKey));
    };

    comments.forEach((comment) => {
      const threadKey = String(comment.thread_id);
      visit(comment, 1, threadKey);
    });

    if (rows.length === 0) {
      return [];
    }

    const header = [
      'Thread ID',
      'Comment ID',
      'Parent Comment ID',
      'Depth',
      'Author',
      'Author Title',
      'Author Email',
      'Recommendation',
      'Private',
      'Created At',
      'Updated At',
      'Comment',
    ];

    const csvContent = [
      header,
      ...rows.map(({ comment, depth, threadKey }) => {
        const authorName =
          comment.author?.full_name ??
          formatMessage({
            id: 'app.views.widgets.dashboards.calibration_dashboard.comments.author.unknown',
            defaultMessage: 'Someone',
          });
        const recommendationDetails = getRecommendationDetails(
          formatMessage,
          comment.recommendation
        );
        const recommendationLabel = recommendationDetails.label ?? '';
        return [
          threadKey,
          comment.id?.toString() ?? '',
          comment.parent_comment_id?.toString() ?? '',
          depth.toString(),
          authorName,
          comment.author?.title ?? '',
          comment.author?.email ?? '',
          recommendationLabel,
          comment.visibility ===
          CalibrationCommentVisibilty.AUTHOR_AND_ADMINISTRATORS
            ? 'Yes'
            : 'No',
          comment.created_at ?? '',
          comment.updated_at ?? '',
          comment.comment ?? '',
        ];
      }),
    ];
    return csvContent;
  }, [comments, isModal, formatMessage]);

  useEffect(() => {
    setActiveRecommendations(() => getRecommendationFilters(recommendation));
  }, [recommendation]);

  const composerRecommendationDefault = useMemo<
    CalibrationRecommendation | undefined
  >(() => {
    return getComposerDefaultRecommendation(activeRecommendations);
  }, [activeRecommendations]);

  const filteredComments = useMemo(() => {
    if (activeRecommendations.size === 0) {
      return comments;
    }
    return comments.filter(
      (comment) =>
        comment.recommendation &&
        activeRecommendations.has(comment.recommendation)
    );
  }, [activeRecommendations, comments]);

  const rootThreadKeys = useMemo(() => {
    return filteredComments.map((comment) => String(comment.thread_id));
  }, [filteredComments]);

  const allThreadsExpanded = useMemo(() => {
    if (rootThreadKeys.length === 0) {
      return false;
    }
    return rootThreadKeys.every((key) => expandedThreads.has(key));
  }, [expandedThreads, rootThreadKeys]);

  const hasComments = filteredComments.length > 0;

  const handleSelectAllRecommendations = useCallback(() => {
    setActiveRecommendations(new Set<CalibrationRecommendation>());
  }, []);

  const handleToggleRecommendation = useCallback(
    (filter: CalibrationRecommendation) => {
      setActiveRecommendations((current) => {
        const next = new Set(current);
        if (next.has(filter)) {
          next.delete(filter);
        } else {
          next.add(filter);
        }
        return next;
      });
    },
    []
  );

  const handleToggleThread = useCallback((threadKey: string) => {
    setExpandedThreads((current) => {
      const next = new Set(current);
      if (next.has(threadKey)) {
        next.delete(threadKey);
      } else {
        next.add(threadKey);
      }
      return next;
    });
  }, []);

  const closeEditor = useCallback(() => {
    setEditingCommentId(null);
    setEditError(null);
    onUnsavedEditChange?.(false);
  }, [onUnsavedEditChange]);

  const openReplyComposer = useCallback(
    (comment: CalibrationComment) => {
      // Reopening the same reply would clear its restored draft
      if (activeReplyTarget === String(comment.id)) {
        return;
      }
      closeEditor();
      setActiveReplyTarget(String(comment.id));
      setReplyText('');
      setReplyIsPrivate(false);
      setReplyError(null);
    },
    [activeReplyTarget, closeEditor]
  );

  // Closing keeps the reply's draft; the composer deletes it when the reply
  // is posted or cancelled
  const closeReplyComposer = useCallback(() => {
    setActiveReplyTarget(null);
    setReplyText('');
    setReplyIsPrivate(false);
    setReplyError(null);
    setIsSubmittingReply(false);
  }, []);

  const handleReplyDraftRestored = useCallback((draft: CommentDraft) => {
    setReplyText((current) => current || draft.content);
    setReplyIsPrivate(draft.isPrivate);
  }, []);

  // One draft per thread, so starting a reply elsewhere doesn't overwrite it
  const getReplyDraftKey = useCallback(
    (parentCommentId: string) =>
      `calibration-comment-reply-draft-${surveyResponseId}-${parentCommentId}`,
    [surveyResponseId]
  );

  const handleSubmitReply = useCallback(
    async (comment: ThreadedCalibrationComment, onPosted: () => void) => {
      const plainContent = stripHtml(replyText ?? '', true).trim();
      if (!plainContent) {
        setReplyError(
          formatMessage({
            id: 'app.views.widgets.dashboards.calibration_dashboard.comments.error.empty',
            defaultMessage: 'Please enter a comment before saving.',
          })
        );
        return;
      }

      setIsSubmittingReply(true);
      setReplyError(null);

      onSubmitComment(
        {
          comment: replyText,
          visibility: replyIsPrivate
            ? CalibrationCommentVisibilty.AUTHOR_AND_ADMINISTRATORS
            : CalibrationCommentVisibilty.AUTHOR_AND_REVIEWERS,
          parent_comment_id: comment.id,
          survey_response: surveyResponseId,
        },
        onPosted,
        (error) => {
          const message =
            error instanceof Error
              ? error.message
              : formatMessage({
                  id: 'app.views.widgets.dashboards.calibration_dashboard.comments.error.generic',
                  defaultMessage:
                    'Unable to save the comment. Please try again.',
                });
          setReplyError(message);
          setIsSubmittingReply(false);
        }
      );
    },
    [
      formatMessage,
      onSubmitComment,
      replyIsPrivate,
      replyText,
      surveyResponseId,
    ]
  );

  const openEditor = useCallback(
    (comment: CalibrationComment) => {
      closeReplyComposer();
      setEditingCommentId(String(comment.id));
      setEditError(null);
      onUnsavedEditChange?.(false);
    },
    [closeReplyComposer, onUnsavedEditChange]
  );

  const handleSubmitEdit = useCallback(
    (
      comment: ThreadedCalibrationComment,
      payload: UpdateCalibrationCommentPayload
    ) => {
      if (!onEditComment) {
        return;
      }
      const plainContent = stripHtml(payload.comment ?? '', true).trim();
      if (!plainContent) {
        setEditError(
          formatMessage({
            id: 'app.views.widgets.dashboards.calibration_dashboard.comments.error.empty',
            defaultMessage: 'Please enter a comment before saving.',
          })
        );
        return;
      }

      setIsSubmittingEdit(true);
      setEditError(null);

      onEditComment(
        comment,
        payload,
        () => {
          setIsSubmittingEdit(false);
          closeEditor();
        },
        (error) => {
          const message =
            error instanceof Error
              ? error.message
              : formatMessage({
                  id: 'app.views.widgets.dashboards.calibration_dashboard.comments.error.generic',
                  defaultMessage:
                    'Unable to save the comment. Please try again.',
                });
          setEditError(message);
          setIsSubmittingEdit(false);
        }
      );
    },
    [closeEditor, formatMessage, onEditComment]
  );

  const openComposer = useCallback(() => {
    if (!canAddComment) {
      return;
    }
    setComposer({
      content: '',
      recommendation: composerRecommendationDefault,
      isPrivate: false,
    });
    setComposerError(null);
  }, [canAddComment, composerRecommendationDefault]);

  const updateComposer = useCallback((changes: Partial<CommentDraft>) => {
    setComposer((current) => current && { ...current, ...changes });
  }, []);

  const handleComposerContentChange = useCallback(
    (content: string) => updateComposer({ content }),
    [updateComposer]
  );

  const handleComposerRecommendationChange = useCallback(
    (recommendation?: CalibrationRecommendation) =>
      updateComposer({ recommendation }),
    [updateComposer]
  );

  const handleComposerIsPrivateChange = useCallback(
    (isPrivate: boolean) => updateComposer({ isPrivate }),
    [updateComposer]
  );

  const closeComposer = useCallback(() => {
    setComposer(null);
    setComposerError(null);
    setIsSubmittingComposer(false);
  }, []);

  // Reopen the composer with the unposted comment once it is fetched
  const handleComposerDraftRestored = useCallback((draft: CommentDraft) => {
    setComposer((current) => current ?? draft);
  }, []);

  const handleComposerSubmit = useCallback(
    async (onPosted: () => void) => {
      if (!composer) {
        return;
      }
      const plainContent = stripHtml(composer.content, true).trim();
      if (!plainContent) {
        setComposerError(
          formatMessage({
            id: 'app.views.widgets.dashboards.calibration_dashboard.comments.error.empty',
            defaultMessage: 'Please enter a comment before saving.',
          })
        );
        return;
      }

      setIsSubmittingComposer(true);
      setComposerError(null);

      onSubmitComment(
        {
          comment: composer.content,
          recommendation: composer.recommendation,
          visibility: composer.isPrivate
            ? CalibrationCommentVisibilty.AUTHOR_AND_ADMINISTRATORS
            : CalibrationCommentVisibilty.AUTHOR_AND_REVIEWERS,
          survey_response: surveyResponseId,
        },
        onPosted,
        (error: any) => {
          const message =
            error instanceof Error
              ? error.message
              : formatMessage({
                  id: 'app.views.widgets.dashboards.calibration_dashboard.comments.error.generic',
                  defaultMessage:
                    'Unable to save the comment. Please try again.',
                });
          setComposerError(message);
          setIsSubmittingComposer(false);
        }
      );
    },
    [composer, formatMessage, onSubmitComment, surveyResponseId]
  );

  const handleToggleVisibility = useCallback(
    (comment: CalibrationComment) => {
      if (!onToggleVisibility) {
        return;
      }
      onToggleVisibility(comment);
    },
    [onToggleVisibility]
  );

  const handleExpandAllThreads = useCallback(() => {
    if (rootThreadKeys.length === 0) {
      return;
    }
    setExpandedThreads((current) => {
      const next = new Set(current);
      rootThreadKeys.forEach((key) => next.add(key));
      return next;
    });
  }, [rootThreadKeys]);

  const handleCollapseAllThreads = useCallback(() => {
    setExpandedThreads(new Set());
  }, []);

  const handleToggleExpandCollapse = useCallback(() => {
    if (allThreadsExpanded) {
      handleCollapseAllThreads();
    } else {
      handleExpandAllThreads();
    }
  }, [allThreadsExpanded, handleCollapseAllThreads, handleExpandAllThreads]);

  const containerStyle = isModal
    ? undefined
    : {
        minWidth: '22rem',
        maxWidth: '28rem',
      };

  const containerClassName = ['p-3', isModal ? 'w-100' : '']
    .filter(Boolean)
    .join(' ');

  const footerClassName = isModal
    ? 'd-flex flex-wrap gap-2 justify-content-between align-items-center mt-3'
    : 'd-flex flex-column gap-3 mt-3';

  return (
    <div style={containerStyle} className={containerClassName}>
      {isModal && (
        <CommentFilters
          participantUniqueId={participant.id}
          activeRecommendations={activeRecommendations}
          onSelectAllRecommendations={handleSelectAllRecommendations}
          onToggleRecommendation={handleToggleRecommendation}
          onToggleShowPrivate={onToggleShowPrivate}
          allThreadsExpanded={allThreadsExpanded}
          onToggleExpandCollapse={handleToggleExpandCollapse}
          formatMessage={formatMessage}
          showPrivateComments={showPrivateComments}
        >
          {hasComments && (
            <ExportButton filename={csvFilename} data={csvData} />
          )}
        </CommentFilters>
      )}

      {hasComments ? (
        <CommentThread
          comments={filteredComments}
          expandedThreads={expandedThreads}
          activeReplyTarget={activeReplyTarget}
          replyText={replyText}
          onReplyTextChange={setReplyText}
          replyIsPrivate={replyIsPrivate}
          onReplyIsPrivateChange={setReplyIsPrivate}
          replyError={replyError}
          isSubmittingReply={isSubmittingReply}
          getReplyDraftKey={getReplyDraftKey}
          onReplyDraftRestored={handleReplyDraftRestored}
          onReplyPendingDraftSaveChange={setHasPendingReplyDraftSave}
          onOpenReplyComposer={openReplyComposer}
          onCloseReplyComposer={closeReplyComposer}
          onSubmitReply={handleSubmitReply}
          onToggleThread={handleToggleThread}
          onToggleVisibility={handleToggleVisibility}
          editingCommentId={editingCommentId}
          editError={editError}
          isSubmittingEdit={isSubmittingEdit}
          onOpenEditor={onEditComment ? openEditor : undefined}
          onCloseEditor={closeEditor}
          onSubmitEdit={handleSubmitEdit}
          onEditDirtyChange={onUnsavedEditChange}
          formatMessage={formatMessage}
          variant={variant}
          canAddComment={canAddComment}
          currentUserId={currentUserId}
          isViewAdmin={isViewAdmin}
          highlightedCommentId={highlightedCommentId}
        />
      ) : (
        <div className="small text-muted">
          <FormattedMessage
            id="app.views.widgets.dashboards.calibration_dashboard.comments.empty"
            defaultMessage="No comments yet."
          />
        </div>
      )}

      {isModal && canAddComment && (
        <div className={footerClassName}>
          <CommentComposer
            isOpen={!!composer}
            composerContent={composer?.content ?? ''}
            onComposerContentChange={handleComposerContentChange}
            composerRecommendation={composer?.recommendation}
            onComposerRecommendationChange={handleComposerRecommendationChange}
            composerIsPrivate={composer?.isPrivate ?? false}
            onComposerIsPrivateChange={handleComposerIsPrivateChange}
            composerError={composerError}
            isSubmitting={isSubmittingComposer}
            onOpen={openComposer}
            onCancel={closeComposer}
            onSubmit={handleComposerSubmit}
            canAddComment={canAddComment}
            draftKey={`calibration-comment-draft-${surveyResponseId}`}
            onDraftRestored={handleComposerDraftRestored}
            onPendingDraftSaveChange={setHasPendingCommentDraftSave}
          />
        </div>
      )}
    </div>
  );
};

export default CalibrationCommentsPopoverContent;
